const normalizeStatus = (value) => String(value || '').trim().toLowerCase();

const isVerified = (capability) => normalizeStatus(capability?.verification_status) === 'verified';

const isUnverifiedOrDetected = (capability) => {
  const status = normalizeStatus(capability?.verification_status);
  return status === 'detected' || status === 'unverified' || !status;
};

export const equipmentCapabilityTone = (capability) => {
  if (isVerified(capability) && capability?.supports_write_workout) return 'ready';
  if (isUnverifiedOrDetected(capability)) return 'warning';
  if (isVerified(capability) || capability?.supports_read_data) return 'neutral';
  return 'muted';
};

export const equipmentDeliveryStatusTone = (status) => {
  const value = normalizeStatus(status);
  if (value === 'ready_to_send') return 'ready';
  if (value === 'requires_user_confirmation') return 'warning';
  if (value === 'blocked_no_verified_capability' || value === 'failed' || value === 'rejected') return 'blocked';
  if (value === 'completed' || value === 'accepted') return 'neutral';
  return 'muted';
};

export const formatEquipmentCapabilityStatus = (capability) => {
  if (isVerified(capability) && capability?.supports_write_workout) {
    return 'profile.wearables.equipmentAutomation.capabilityVerifiedDelivery';
  }
  if (isVerified(capability) && capability?.supports_read_data) {
    return 'profile.wearables.equipmentAutomation.capabilityVerifiedReadOnly';
  }
  if (isUnverifiedOrDetected(capability)) {
    return 'profile.wearables.equipmentAutomation.capabilityUnverified';
  }
  return 'profile.wearables.equipmentAutomation.capabilityUnavailable';
};

export const summarizeEquipmentCapabilities = (capabilities = []) => {
  const list = Array.isArray(capabilities) ? capabilities : [];
  const verified = list.filter(isVerified);
  const unverifiedOrDetected = list.filter(isUnverifiedOrDetected);
  const readDataSupported = verified.filter((capability) => Boolean(capability?.supports_read_data)).length;
  const workoutDeliverySupported = verified.filter((capability) => Boolean(capability?.supports_write_workout)).length;
  const liveControlSupported = verified.filter((capability) => Boolean(capability?.supports_live_control)).length;

  return {
    totalCount: list.length,
    verifiedCount: verified.length,
    unverifiedOrDetectedCount: unverifiedOrDetected.length,
    readDataSupportedCount: readDataSupported,
    workoutDeliverySupportedCount: workoutDeliverySupported,
    liveControlSupportedCount: liveControlSupported,
    hasVerifiedWorkoutDelivery: workoutDeliverySupported > 0,
    hasUnverifiedCapability: unverifiedOrDetected.length > 0,
    latestCapability: list[0] || null,
    primaryMessageKey: workoutDeliverySupported > 0
      ? 'profile.wearables.equipmentAutomation.deliveryAvailableMessage'
      : unverifiedOrDetected.length > 0
        ? 'profile.wearables.equipmentAutomation.unverifiedMessage'
        : 'profile.wearables.equipmentAutomation.notEnabledMessage',
  };
};

export const summarizeEquipmentDeliveries = (deliveries = []) => {
  const list = Array.isArray(deliveries) ? deliveries : [];
  const latestDelivery = list[0] || null;
  const latestStatus = normalizeStatus(latestDelivery?.status);
  const fallbackModes = list.reduce((acc, delivery) => {
    const mode = normalizeStatus(delivery?.delivery_mode);
    if (mode) acc[mode] = (acc[mode] || 0) + 1;
    return acc;
  }, {});

  const statusMessageKeys = {
    blocked_no_verified_capability: 'profile.wearables.equipmentAutomation.deliveryBlockedMessage',
    requires_user_confirmation: 'profile.wearables.equipmentAutomation.deliveryConfirmationMessage',
    ready_to_send: 'profile.wearables.equipmentAutomation.deliveryReadyMessage',
  };

  return {
    totalCount: list.length,
    latestDelivery,
    latestStatus,
    latestStatusTone: equipmentDeliveryStatusTone(latestStatus),
    latestStatusMessageKey: statusMessageKeys[latestStatus] || 'profile.wearables.equipmentAutomation.noDeliveryMessage',
    manualFallbackAvailable: list.some((delivery) => delivery?.manual_fallback_available),
    fallbackModes,
  };
};
